import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Grid,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Chip,
  Divider,
  Skeleton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { useDispatch, useSelector } from 'react-redux';
import { userSelector } from '../../../Components/user-redux/selector';
import { getCandidateDocuments } from '../../../Components/redux/action';
import { appSelector } from '../../../Components/redux/selector';
import Key from '../../../../src/clientVariables.json';
import PdfIcon from '../../Candidates/pdficon';
import WordIcon from '../../Candidates/wordicon';
import url from '../../../api/apisList';
import { toast } from 'react-toastify';
import { Worker, Viewer } from '@react-pdf-viewer/core';

const CandidatesDocuments = () => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [docsLoading, setDocsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { candidateDocuments } = useSelector(appSelector);
  const { user } = useSelector(userSelector);
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const loadDocuments = async ({ silent = false } = {}) => {
    if (!user?.candidate_id || !user?.id) {
      setDocsLoading(false);
      return;
    }
    if (silent) {
      setIsRefreshing(true);
    } else {
      setDocsLoading(true);
    }
    try {
      await dispatch(
        getCandidateDocuments(user.candidate_id, user.id, { clearFirst: !silent })
      );
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      if (silent) {
        setIsRefreshing(false);
      } else {
        setDocsLoading(false);
      }
    }
  };

  useEffect(() => {
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.candidate_id, user?.id, dispatch]);

  useEffect(() => {
    if (candidateDocuments?.length > 0) {
      const enhancedDocs = candidateDocuments.map((doc) => {
        const userId = doc.user_id ?? user?.id;
        const candId = doc.cand_id ?? user?.candidate_id;
        const fileName = doc.name ?? doc.attachment;
        const type = getFileType(fileName);
        const isImage = type === 'image';
        const thumbnail = isImage
          ? url('candidate_attachment_file')(userId, candId, fileName)
          : null;

        return {
          ...doc,
          user_id: userId,
          cand_id: candId,
          name: fileName,
          type,
          thumbnail,
        };
      });

      setSelectedFiles(enhancedDocs);
    } else if (!docsLoading && !isRefreshing) {
      setSelectedFiles([]);
    }
  }, [candidateDocuments, docsLoading, isRefreshing, user?.id, user?.candidate_id]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const filedata = new FormData();
    filedata.append('file', file);
    filedata.append('userid', user?.id);
    filedata.append('candidateid', user.candidate_id);
    filedata.append('filename', file?.name);
    filedata.append('file_size', file?.size);
    filedata.append('file_type', file?.type);
    filedata.append('attachment_type', 'Candidate_attachment');
    filedata.append('attachment_name', file?.name);
    filedata.append('file_created', file?.lastModifiedDate);

    try {
      const response = await fetch(`${Key.domain}/candidate/upload_document`, {
        method: 'POST',
        body: filedata,
      });
      if (response.ok) {
        await loadDocuments({ silent: true });
        toast.success('Document uploaded successfully');
      } else {
        console.error('Upload failed:', response.statusText);
        toast.error('Upload failed. Please try again.');
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      toast.error('Error uploading file');
    } finally {
      e.target.value = '';
    }
  };

  const handleDelete = async (attachmentId) => {
    try {
      const response = await fetch(`${Key.domain}/candidate/delete_document`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          candidateid: user.candidate_id,
          userid: user.id,
          attachment_id: attachmentId,
        }),
      });

      const result = await response.json();
      if (result.success === 1) {
        toast.success('Document deleted successfully');
        await loadDocuments({ silent: true });
      } else {
        toast.error('Failed to delete document: ' + (result.error?.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Error deleting document: ' + error.message);
    }
    setOpenDialog(false);
    setFileToDelete(null);
  };

  const confirmDelete = (attachmentId, fileName) => {
    setFileToDelete({ attachmentId, fileName });
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setFileToDelete(null);
  };

  const getFileTypeIcon = (fileType, thumbnail) => {
    switch (fileType) {
      case 'pdf':
        return <PdfIcon style={{ fontSize: '48px', color: '#ef4444' }} />;
      case 'word':
        return <WordIcon style={{ fontSize: '48px', color: '#3b82f6' }} />;
      case 'image':
        return (
          <img
            src={thumbnail}
            alt="Thumbnail"
            style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: '8px' }}
          />
        );
      default:
        return <InsertDriveFileIcon sx={{ fontSize: 48, color: '#94a3b8' }} />;
    }
  };

  const getFileType = (fileName) => {
    const extension = fileName?.split('.').pop().toLowerCase();
    if (['pdf'].includes(extension)) return 'pdf';
    if (['doc', 'docx'].includes(extension)) return 'word';
    if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) return 'image';
    return 'other';
  };

  const getTypeChipColor = (type) => {
    switch (type) {
      case 'pdf':
        return { bg: '#fee2e2', color: '#b91c1c' };
      case 'word':
        return { bg: '#dbeafe', color: '#1d4ed8' };
      case 'image':
        return { bg: '#d1fae5', color: '#047857' };
      default:
        return { bg: '#f1f5f9', color: '#64748b' };
    }
  };

  const openFileInNewTab = (fileData) => {
    const apiUrl = url('candidate_attachment_file')(
      fileData?.user_id,
      fileData?.cand_id,
      fileData?.name
    );
    window.open(apiUrl, '_blank');
  };

  const pageHeight = 'calc(100vh - 67px)';

  return (
    <Box
      sx={{
        mx: { xs: -1, md: -2 },
        px: 0.5,
        pt: 0.5,
        height: pageHeight,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: '#f4f7fb',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          flexShrink: 0,
          borderRadius: '12px',
          border: '1px solid #e3eaf3',
          bgcolor: '#fff',
          px: 2.5,
          py: 2,
          mb: 0.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: '10px',
              bgcolor: 'rgba(25, 118, 210, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <DescriptionOutlinedIcon sx={{ color: '#1976d2' }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#0f172a' }}>
              Candidate Documents
            </Typography>
            <Typography sx={{ fontSize: '13px', color: '#64748b' }}>
              {selectedFiles.length} document{selectedFiles.length === 1 ? '' : 's'} uploaded
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          startIcon={<CloudUploadOutlinedIcon />}
          onClick={handleUploadClick}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '10px',
            px: 2.5,
            boxShadow: 'none',
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.35)',
            },
          }}
        >
          Upload Files
        </Button>
        <input
          type="file"
          style={{ display: 'none' }}
          ref={fileInputRef}
          onChange={handleFileChange}
        />
      </Paper>

      <Paper
        elevation={0}
        sx={{
          flex: 1,
          minHeight: 0,
          borderRadius: '12px',
          border: '1px solid #e3eaf3',
          bgcolor: '#fff',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #eef2f7', flexShrink: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#334155' }}>
            Your documents
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            p: 2,
          }}
        >
          {docsLoading || (isRefreshing && selectedFiles.length === 0) ? (
            <Grid container spacing={2}>
              {Array.from({ length: 8 }).map((_, index) => (
                <Grid item key={`doc-skeleton-${index}`} xs={12} sm={6} md={4} lg={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: '12px',
                      overflow: 'hidden',
                      minHeight: 220,
                      bgcolor: '#fff',
                      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
                    }}
                  >
                    <Skeleton variant="rectangular" height={140} animation="wave" />
                    <Box sx={{ p: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Skeleton variant="text" width="60%" height={22} />
                        <Skeleton variant="rounded" width={40} height={22} />
                      </Box>
                      <Skeleton variant="text" width="80%" height={18} />
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          ) : selectedFiles.length === 0 ? (
            <Box
              sx={{
                height: '100%',
                minHeight: 280,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#94a3b8',
                gap: 1,
              }}
            >
              <CloudUploadOutlinedIcon sx={{ fontSize: 48, color: '#cbd5e1' }} />
              <Typography sx={{ fontWeight: 600, color: '#64748b' }}>No documents yet</Typography>
              <Typography sx={{ fontSize: '13px', mb: 1 }}>
                Upload visa, licence, or verification documents
              </Typography>
              <Button
                variant="outlined"
                startIcon={<CloudUploadOutlinedIcon />}
                onClick={handleUploadClick}
                sx={{
                  textTransform: 'none',
                  borderRadius: '10px',
                  borderColor: '#bfdbfe',
                  color: '#1976d2',
                  '&:hover': { borderColor: '#93c5fd', bgcolor: 'rgba(25, 118, 210, 0.04)' },
                }}
              >
                Upload Files
              </Button>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {selectedFiles.map((fileData, index) => {
                const fileType = getFileType(fileData?.name);
                const chipColors = getTypeChipColor(fileType);

                return (
                  <Grid item key={fileData.attachment_id ?? index} xs={12} sm={6} md={4} lg={3}>
                    <Paper
                      elevation={0}
                      onClick={() => openFileInNewTab(fileData)}
                      sx={{
                        borderRadius: '12px',
                        border: 'none',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        height: '100%',
                        minHeight: 220,
                        display: 'flex',
                        flexDirection: 'column',
                        bgcolor: '#fff',
                        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
                        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                        position: 'relative',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.1)',
                          '& .doc-actions': { opacity: 1 },
                        },
                      }}
                    >
                      <Box
                        className="doc-actions"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 2,
                          display: 'flex',
                          gap: 0.5,
                          opacity: { xs: 1, sm: 0 },
                          transition: 'opacity 0.2s ease',
                        }}
                      >
                        <IconButton
                          size="small"
                          sx={{
                            bgcolor: 'rgba(255,255,255,0.95)',
                            border: '1px solid #e2e8f0',
                            '&:hover': { bgcolor: '#fff' },
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            openFileInNewTab(fileData);
                          }}
                          title="Open"
                        >
                          <OpenInNewIcon sx={{ fontSize: 16, color: '#64748b' }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          sx={{
                            bgcolor: 'rgba(255,255,255,0.95)',
                            border: '1px solid #fee2e2',
                            '&:hover': { bgcolor: '#fef2f2' },
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            confirmDelete(fileData.attachment_id, fileData.name);
                          }}
                          title="Delete"
                        >
                          <DeleteIcon sx={{ fontSize: 16, color: '#ef4444' }} />
                        </IconButton>
                      </Box>

                      <Box
                        sx={{
                          flex: 1,
                          minHeight: 140,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: '#f8fafc',
                          p: 1.5,
                          overflow: 'hidden',
                        }}
                      >
                        {fileType === 'image' ? (
                          <Box
                            component="img"
                            src={fileData.thumbnail}
                            alt="Thumbnail"
                            sx={{
                              width: '100%',
                              height: 140,
                              objectFit: 'cover',
                              borderRadius: '8px',
                            }}
                          />
                        ) : fileType === 'pdf' ? (
                          <Box
                            sx={{
                              width: '100%',
                              height: 140,
                              overflow: 'hidden',
                              borderRadius: '8px',
                              bgcolor: '#fff',
                              border: '1px solid #eef2f7',
                            }}
                          >
                            <Worker
                              workerUrl={`https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`}
                            >
                              <Viewer
                                fileUrl={url('candidate_attachment_file')(
                                  fileData?.user_id,
                                  fileData?.cand_id,
                                  fileData?.name
                                )}
                                defaultScale={0.27}
                              />
                            </Worker>
                          </Box>
                        ) : (
                          getFileTypeIcon(fileType, fileData.thumbnail)
                        )}
                      </Box>

                      <Divider sx={{ borderColor: '#eef2f7' }} />

                      <Box sx={{ px: 1.5, py: 1.25 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            title={fileData.attachment_name || fileData?.name}
                            sx={{
                              fontSize: '13px',
                              fontWeight: 600,
                              color: '#0f172a',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              flex: 1,
                            }}
                          >
                            {fileData.attachment_name || fileData?.name}
                          </Typography>
                          <Chip
                            label={fileType.toUpperCase()}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '10px',
                              fontWeight: 700,
                              bgcolor: chipColors.bg,
                              color: chipColors.color,
                            }}
                          />
                        </Box>
                        <Typography
                          title={fileData?.name}
                          sx={{
                            fontSize: '12px',
                            color: '#94a3b8',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {fileData?.name}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      </Paper>

      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        PaperProps={{
          sx: { borderRadius: '12px', p: 0.5, minWidth: 360 },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>Delete document?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '14px' }}>
            Are you sure you want to delete{' '}
            <Typography component="span" sx={{ fontWeight: 600, color: '#0f172a' }}>
              {fileToDelete?.fileName}
            </Typography>
            ? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleDialogClose}
            sx={{ textTransform: 'none', borderRadius: '8px', color: '#64748b' }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleDelete(fileToDelete.attachmentId)}
            variant="contained"
            color="error"
            disableElevation
            sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CandidatesDocuments;
